import React from 'react';
/* A 112px re-export of the brand mark: the full-size logo.jpg is 91KB
   for a 34px slot, which is the page's only real asset waste. */
import logoMark from '../../assets/images/logo-mark.jpg';
import { HERO, HERO_CARDS } from '../data/content';
import useMotionPrefs from '../hooks/useMotionPrefs';
import usePointerVars from '../hooks/usePointerVars';
import useScrollVar from '../hooks/useScrollVar';
import MagneticButton from '../components/MagneticButton';
import { Pin, Spark } from '../components/icons';

/* ==================================================================
   Hero
   ------------------------------------------------------------------
   Full-screen opening. Three independent motion layers:

   1. load     - headline words, sub-copy, CTAs and stage animate in
                 on mount with a staggered delay (CSS keyframes).
   2. pointer  - --hs-mx / --hs-my on the section; the ambient light,
                 the blobs and every floating card read them at their
                 own depth, so the ecosystem has real parallax.
   3. scroll   - --hs-p on the inner wrapper drifts the whole hero up
                 and dims it as the story below takes over.

   Each floating card is two nested elements on purpose: the outer one
   owns the cursor parallax transform, the inner one owns the endless
   float keyframes, so the two never fight over `transform`.
   ================================================================== */

/* Connector endpoints are derived from each card's position so the
   lines always land on the card, whatever the stage size. */
function anchorFor(card) {
  return { x: card.x + 13, y: card.y + 7 };
}

export default function Hero() {
  const { reduced, pointer } = useMotionPrefs();
  const stageRef = usePointerVars({ disabled: reduced || !pointer });
  const driftRef = useScrollVar({ mode: 'exit', varName: '--hs-p', disabled: reduced, resetValue: 0 });

  let wordIndex = 0;

  return (
    <section className="hs-hero" ref={stageRef}>
      <div className="hs-hero__ambience" aria-hidden="true">
        <span className="hs-blob hs-blob--a" />
        <span className="hs-blob hs-blob--b" />
        <span className="hs-blob hs-blob--c" />
        <span className="hs-hero__light" />
        <span className="hs-grid" />
      </div>

      <div className="hs-hero__inner" ref={driftRef}>
        <div className="hs-hero__head">
          <p className="hs-hero__eyebrow hs-load" style={{ '--hs-i': 0 }}>
            <Spark />
            {HERO.eyebrow}
          </p>

          <h1 className="hs-hero__title">
            {HERO.headlineLines.map((line, lineIndex) => (
              <span
                className={`hs-hero__line ${line.accent ? 'hs-hero__line--accent' : ''}`}
                key={lineIndex}
              >
                {line.words.map((word) => {
                  wordIndex += 1;
                  return (
                    <span className="hs-word" key={word + wordIndex}>
                      <span className="hs-word__in" style={{ '--hs-i': wordIndex }}>
                        {word}
                      </span>
                    </span>
                  );
                })}
              </span>
            ))}
          </h1>

        </div>

        <div className="hs-hero__row">
          <div className="hs-hero__pitch">
            <p className="hs-hero__sub hs-load" style={{ '--hs-i': 7 }}>{HERO.sub}</p>

            <div className="hs-hero__ctas hs-load" style={{ '--hs-i': 8 }}>
              <MagneticButton to={HERO.primaryCta.to} variant="primary" size="lg" arrow>
                {HERO.primaryCta.label}
              </MagneticButton>
              <MagneticButton to={HERO.secondaryCta.to} variant="ghost" size="lg">
                {HERO.secondaryCta.label}
              </MagneticButton>
            </div>

            <p className="hs-hero__foot hs-load" style={{ '--hs-i': 9 }}>
              Built for students from IITs, NITs and India&rsquo;s top campuses.
            </p>
          </div>

          <div className="hs-stage hs-load" style={{ '--hs-i': 6 }}>
            <svg
              className="hs-stage__wires"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {HERO_CARDS.map((card) => {
                const a = anchorFor(card);
                return (
                  <line
                    key={card.id}
                    x1="50"
                    y1="50"
                    x2={a.x}
                    y2={a.y}
                    vectorEffect="non-scaling-stroke"
                    style={{ '--hs-i': card.priority }}
                  />
                );
              })}
            </svg>

            <div className="hs-hub">
              <span className="hs-hub__ring" aria-hidden="true" />
              <span className="hs-hub__ring hs-hub__ring--wide" aria-hidden="true" />
              <div className="hs-hub__card">
                <img src={logoMark} alt="HyrSkill" width="44" height="44" decoding="async" />
                <p className="hs-hub__name">HyrSkill</p>
                <p className="hs-hub__meta">talent &rarr; opportunity</p>
              </div>
            </div>

            {HERO_CARDS.map((card, i) => (
              <div
                key={card.id}
                className={`hs-orbit hs-orbit--p${card.priority}`}
                style={{
                  '--hs-x': `${card.x}%`,
                  '--hs-y': `${card.y}%`,
                  '--hs-d': card.depth,
                  '--hs-i': i,
                }}
              >
                <div className="hs-orbit__float">
                  <article className={`hs-float-card hs-float-card--${card.tone}`}>
                    <p className="hs-float-card__role">{card.role}</p>
                    <p className="hs-float-card__meta"><Pin size={13} />{card.meta}</p>
                  </article>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hs-hero__scrollcue hs-load" style={{ '--hs-i': 10 }} aria-hidden="true">
        <span className="hs-hero__scrollcue-rail" />
        Scroll
      </div>
    </section>
  );
}
